import React, { Component } from 'react';

import { getUnsyncedProducts } from '../utils/mlApi';
import { PRODUCT_TYPE } from '../utils/enums';

// Productos del catálogo SIN ninguna publicación de MercadoLibre vinculada.
//
// El vínculo se carga a mano en la ficha del producto, así que lo que esta grilla contesta es
// "¿qué me quedó sin poner a la venta en ML?" — la pregunta que antes había que resolver a ojo
// producto por producto. La acción es abrir la ficha (en otra pestaña, para no perder la grilla)
// y pegar ahí el MLU.
//
// Dos controles independientes porque son dos preguntas distintas:
//   - Stock: lo urgente es lo que tiene stock (se puede vender hoy y no está publicado).
//   - Ocultos (display=0): normalmente ruido, pero hay escenarios donde hay que verlos.

const money = (value) => (value == null ? '—' : `$${Number(value).toLocaleString('de-DE')}`);

const CSV_COLUMNS = [
    { key: 'prefix', label: 'Prefijo' },
    { key: 'code', label: 'Código' },
    { key: 'name', label: 'Nombre' },
    { key: 'sku', label: 'SKU' },
    { key: 'barCode', label: 'Código de barras' },
    { key: 'stock', label: 'Stock' },
    { key: 'price', label: 'Precio' },
    { key: 'display', label: 'Visible' },
    { key: 'productType', label: 'Tipo' },
];

function toCsv(rows) {
    const escape = (value) => {
        const text = value == null ? '' : String(value);
        return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    return [
        CSV_COLUMNS.map((c) => c.label).join(','),
        ...rows.map((row) => CSV_COLUMNS.map((c) => escape(row[c.key])).join(',')),
    ].join('\n');
}

class MlUnsyncedProductsComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            items: [],
            totals: { total: 0, visible: 0, visibleWithStock: 0, hidden: 0, hiddenWithStock: 0 },
            loaded: false,
            error: null,
            withStock: true,
            includeHidden: false,
            searchInput: '',
            search: '',
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        const { withStock, includeHidden, search } = this.state;
        this.setState({ loaded: false, error: null });
        getUnsyncedProducts({ withStock, includeHidden, search })
            .then(({ items, totals }) => this.setState({ items, totals, loaded: true }))
            .catch((error) => this.setState({ loaded: true, error: error.message }));
    }

    toggleStock = (withStock) => {
        if (withStock === this.state.withStock) return;
        this.setState({ withStock }, this.load);
    }

    toggleHidden = (event) => {
        this.setState({ includeHidden: event.target.checked }, this.load);
    }

    submitSearch = (event) => {
        event.preventDefault();
        this.setState({ search: this.state.searchInput.trim() }, this.load);
    }

    clearSearch = () => {
        if (!this.state.search && !this.state.searchInput) return;
        this.setState({ searchInput: '', search: '' }, this.load);
    }

    exportCsv = () => {
        const csv = toCsv(this.state.items);
        const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'ml-sin-sincronizar.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    renderRow = (item, index) => (
        <tr key={`${item.prefix}-${item.code}`} style={{ background: index % 2 === 0 ? '#fff' : '#fcfcfc' }}>
            <td>
                <div>{item.name || '—'}</div>
                <small className="text-muted">{item.prefix}{item.code}</small>
            </td>
            <td>{item.sku || <span className="text-muted">—</span>}</td>
            <td>{PRODUCT_TYPE[item.productType] || item.productType}</td>
            <td className="text-center">
                <span className={`badge ${Number(item.stock) > 0 ? 'badge-success' : 'badge-light'}`}>{item.stock}</span>
            </td>
            <td className="text-right">{money(item.price)}</td>
            <td className="text-center">
                {Number(item.display) === 1
                    ? <span className="badge badge-light">Visible</span>
                    : <span className="badge badge-secondary">Oculto</span>}
            </td>
            <td>
                <a className="btn btn-sm btn-white" href={`/editProduct?code=${item.code}`}
                    target="_blank" rel="noopener noreferrer"
                    title="Abrir la ficha en otra pestaña para vincular la publicación">
                    Abrir ficha
                </a>
            </td>
        </tr>
    );

    render() {
        const { items, totals, loaded, error, withStock, includeHidden, searchInput, search } = this.state;
        const hiddenCount = withStock ? totals.hiddenWithStock : totals.hidden;

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <div className="d-flex flex-wrap align-items-center">
                        <ul className="nav nav-pills mr-3 mb-0">
                            <li className="nav-item">
                                <button type="button"
                                    className={`btn btn-sm mr-1 ${withStock ? 'btn-primary' : 'btn-white'}`}
                                    onClick={() => this.toggleStock(true)}>
                                    Con stock
                                </button>
                            </li>
                            <li className="nav-item">
                                <button type="button"
                                    className={`btn btn-sm mr-1 ${!withStock ? 'btn-primary' : 'btn-white'}`}
                                    onClick={() => this.toggleStock(false)}>
                                    Todos
                                </button>
                            </li>
                        </ul>

                        <div className="custom-control custom-checkbox mr-3">
                            <input type="checkbox" className="custom-control-input" id="mlIncludeHidden"
                                checked={includeHidden} onChange={this.toggleHidden} />
                            <label className="custom-control-label" htmlFor="mlIncludeHidden">
                                Mostrar ocultos {hiddenCount > 0 && <span className="text-muted">({hiddenCount})</span>}
                            </label>
                        </div>

                        <form className="form-inline mr-auto" onSubmit={this.submitSearch}>
                            <input type="text" className="form-control form-control-sm mr-1"
                                placeholder="Nombre, código o SKU"
                                value={searchInput}
                                onChange={(event) => this.setState({ searchInput: event.target.value })} />
                            <button type="submit" className="btn btn-sm btn-white mr-1">Buscar</button>
                            {search && (
                                <button type="button" className="btn btn-sm btn-link" onClick={this.clearSearch}>Limpiar</button>
                            )}
                        </form>

                        <button type="button" className="btn btn-sm btn-white"
                            disabled={items.length === 0} onClick={this.exportCsv}>
                            Exportar CSV
                        </button>
                    </div>
                </div>
                <div className="card-body p-0">
                    {error && <div className="alert alert-danger m-3">{error}</div>}
                    <p className="text-muted px-3 pt-3 mb-2">
                        Productos sin ninguna publicación de ML vinculada. El vínculo se carga en la ficha
                        del producto (campo de publicaciones, formato <code>MLU123</code>).
                        {' '}<strong>{totals.visibleWithStock}</strong> visibles con stock ·
                        {' '}<strong>{totals.visible}</strong> visibles ·
                        {' '}<strong>{totals.total}</strong> en total.
                    </p>
                    {!loaded ? (
                        <p className="px-3 pb-3">Cargando…</p>
                    ) : items.length === 0 ? (
                        <p className="px-3 pb-3">
                            {search
                                ? 'Ningún producto sin sincronizar coincide con la búsqueda.'
                                : 'No hay productos sin sincronizar con estos filtros.'}
                        </p>
                    ) : (
                        <React.Fragment>
                            <p className="text-muted px-3 mb-2"><small>Mostrando {items.length} producto(s).</small></p>
                            <table className="table mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th scope="col" className="border-0">Producto</th>
                                        <th scope="col" className="border-0">SKU</th>
                                        <th scope="col" className="border-0">Tipo</th>
                                        <th scope="col" className="border-0 text-center">Stock</th>
                                        <th scope="col" className="border-0 text-right">Precio</th>
                                        <th scope="col" className="border-0 text-center">Estado</th>
                                        <th scope="col" className="border-0">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => this.renderRow(item, index))}
                                </tbody>
                            </table>
                        </React.Fragment>
                    )}
                </div>
            </div>
        );
    }
}

export default MlUnsyncedProductsComponent;
